import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../url';
import { useTenant } from '../context/TenantContext';
import './TenantManagement.css';
import { 
  LuBuilding2, 
  LuSearch, 
  LuPlus, 
  LuSlidersHorizontal, 
  LuExternalLink, 
  LuCheck, 
  LuX, 
  LuPencil, 
  LuCopy, 
  LuUsers,
  LuShieldAlert,
  LuUpload,
  LuImage,
  LuTrash2
} from 'react-icons/lu';

const DEFAULT_MODULE_OPTIONS = [
  'Recruitment',
  'ATS',
  'Job Submissions',
  'Candidate Manage',
  'Invoice',
  'Timesheet',
  'H1B',
  'CRM'
];

const TenantManagement = () => {
  const navigate = useNavigate();
  const { companyId, refreshBranding } = useTenant();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null);

  // Module Management Modal State
  const [moduleModalTenant, setModuleModalTenant] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]);
  const [savingModules, setSavingModules] = useState(false);

  // Add Company Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addingCompany, setAddingCompany] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({
    tenantId: '',
    companyName: '',
    logoUrl: '',
    primaryColor: '#1976D2',
    allowedModules: ['Recruitment', 'ATS'],
    adminEmail: '',
    adminPassword: '',
    adminFirstName: '',
    adminLastName: ''
  });

  // Edit Company Modal State
  const [editingTenant, setEditingTenant] = useState(null);
  const [updatingCompany, setUpdatingCompany] = useState(false);

  // Safe error formatting helper to prevent React object child crashes
  const formatErrorMessage = (err) => {
    if (!err) return 'An unexpected error occurred';
    if (typeof err === 'string') return err;
    if (typeof err === 'object') {
      if (err.message && typeof err.message === 'string') return err.message;
      if (err.error && typeof err.error === 'object' && err.error.message) return err.error.message;
      if (err.error && typeof err.error === 'string') return err.error;
      try {
        return JSON.stringify(err);
      } catch (e) {
        return 'An error occurred';
      }
    }
    return String(err);
  };

  const showToast = (message, type = 'success') => {
    const safeMessage = typeof message === 'object' ? formatErrorMessage(message) : String(message || '');
    setToast({ message: safeMessage, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Security Check: Only Prophecy Admins can view this page
  const userRole = (localStorage.getItem('role') || '').toLowerCase();
  const isProphecy = !companyId || companyId.toLowerCase() === 'prophecy';
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/api/saas/tenants`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-company-id': 'prophecy'
        }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.tenants)) {
        setTenants(data.tenants);
      } else {
        showToast(data.error || 'Failed to load companies', 'error');
      }
    } catch (err) {
      console.error('Fetch tenants error:', err);
      showToast('Network error while loading companies', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isProphecy || !isAdmin) {
      navigate('/recruitment-dashboard');
      return;
    }
    fetchTenants();
  }, [isProphecy, isAdmin, navigate, fetchTenants]);

  // Open Modules Modal
  const handleOpenModuleModal = (tenant) => {
    setModuleModalTenant(tenant);
    setSelectedModules(tenant.allowedModules || []);
  };

  // Toggle Module in Modal
  const handleToggleModuleInModal = (modName) => {
    setSelectedModules(prev => {
      if (prev.some(m => m.toLowerCase() === modName.toLowerCase())) {
        return prev.filter(m => m.toLowerCase() !== modName.toLowerCase());
      } else {
        return [...prev, modName];
      }
    });
  };

  // Save Modules
  const handleSaveModules = async () => {
    if (!moduleModalTenant) return;
    try {
      setSavingModules(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/api/saas/tenants/${encodeURIComponent(moduleModalTenant.tenantId)}/modules`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-company-id': 'prophecy'
        },
        body: JSON.stringify({ modules: selectedModules })
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Modules updated successfully for ${moduleModalTenant.companyName}!`);
        setModuleModalTenant(null);
        fetchTenants();
      } else {
        showToast(data.error || 'Failed to update modules', 'error');
      }
    } catch (err) {
      console.error('Save modules error:', err);
      showToast('Error saving module configuration', 'error');
    } finally {
      setSavingModules(false);
    }
  };

  // Add Company Submit
  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!newCompanyForm.tenantId.trim() || !newCompanyForm.companyName.trim()) {
      showToast('Tenant ID and Company Name are required', 'error');
      return;
    }

    try {
      setAddingCompany(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/api/saas/tenants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-company-id': 'prophecy'
        },
        body: JSON.stringify(newCompanyForm)
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Company '${newCompanyForm.companyName}' onboarded successfully!`);
        setIsAddModalOpen(false);
        setNewCompanyForm({
          tenantId: '',
          companyName: '',
          logoUrl: '',
          primaryColor: '#1976D2',
          allowedModules: ['Recruitment', 'ATS'],
          adminEmail: '',
          adminPassword: '',
          adminFirstName: '',
          adminLastName: ''
        });
        fetchTenants();
      } else {
        showToast(data.error || 'Failed to onboard company', 'error');
      }
    } catch (err) {
      console.error('Add company error:', err);
      showToast('Network error while onboarding company', 'error');
    } finally {
      setAddingCompany(false);
    }
  };

  // Edit Company Submit
  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    if (!editingTenant) return;
    try {
      setUpdatingCompany(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/api/saas/tenants/${encodeURIComponent(editingTenant.tenantId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-company-id': 'prophecy'
        },
        body: JSON.stringify({
          companyName: editingTenant.companyName,
          logoUrl: editingTenant.logoUrl,
          primaryColor: editingTenant.primaryColor,
          status: editingTenant.status
        })
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Details updated for ${editingTenant.companyName}`);
        try {
          const tid = (editingTenant.tenantId || '').toLowerCase().trim();
          localStorage.removeItem(`saas_config_${tid}`);
          const currentComp = (companyId || localStorage.getItem('company_id') || 'prophecy').toLowerCase().trim();
          if (typeof refreshBranding === 'function' && tid === currentComp) {
            refreshBranding();
          }
        } catch (e) {}
        setEditingTenant(null);
        fetchTenants();
      } else {
        showToast(data.error || 'Failed to update company details', 'error');
      }
    } catch (err) {
      console.error('Update company error:', err);
      showToast('Error updating company details', 'error');
    } finally {
      setUpdatingCompany(false);
    }
  };

  // Copy Login Link Helper
    const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleLogoUpload = async (file, isEdit = false) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Please select a valid image file (.png, .jpg, .svg, .webp)', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('Logo file size must be less than 5MB', 'error');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const tenantSlug = isEdit ? editingTenant?.tenantId : newCompanyForm.tenantId;
      if (tenantSlug) {
        formData.append('tenantId', tenantSlug);
      }

      const token = localStorage.getItem('token');
      const uploadUrl = tenantSlug
        ? `${BASE_URL}/api/saas/upload-logo?tenantId=${encodeURIComponent(tenantSlug)}`
        : `${BASE_URL}/api/saas/upload-logo`;
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-company-id': 'prophecy'
        },
        body: formData
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to upload logo');
      }

      if (isEdit) {
        setEditingTenant(prev => ({ ...prev, logoUrl: data.logoUrl }));
      } else {
        setNewCompanyForm(prev => ({ ...prev, logoUrl: data.logoUrl }));
      }
      showToast('Logo uploaded successfully!', 'success');
    } catch (err) {
      console.error('Error uploading logo:', err);
      showToast(err.message || 'Error uploading logo', 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const [deletingTenantId, setDeletingTenantId] = useState(null);

  const handleDeleteCompany = async (tenant) => {
    if (!tenant || tenant.isHost || tenant.tenantId === 'prophecy') {
      showToast('Host Prophecy ERP system cannot be deleted', 'error');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete "${tenant.companyName || tenant.tenantId}"?\n\nThis will remove the company and all its module permissions. This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setDeletingTenantId(tenant.tenantId);
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/api/saas/tenants/${encodeURIComponent(tenant.tenantId)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-company-id': 'prophecy'
        }
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to delete company');
      }

      showToast(data.message || `Deleted ${tenant.companyName}`, 'success');
      try {
        localStorage.removeItem(`saas_config_${tenant.tenantId}`);
      } catch (e) {}
      fetchTenants();
    } catch (err) {
      console.error('Delete company error:', err);
      showToast(err.message || 'Error deleting company', 'error');
    } finally {
      setDeletingTenantId(null);
    }
  };

  const handleDeleteLogo = async (isEdit = false) => {
    const currentLogo = isEdit ? editingTenant?.logoUrl : newCompanyForm.logoUrl;
    if (!currentLogo) return;

    try {
      const token = localStorage.getItem('token');
      const tenantSlug = isEdit ? editingTenant?.tenantId : newCompanyForm.tenantId;

      await fetch(`${BASE_URL}/api/saas/delete-logo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-company-id': 'prophecy'
        },
        body: JSON.stringify({
          logoUrl: currentLogo,
          tenantId: isEdit ? tenantSlug : null
        })
      });

      if (isEdit) {
        setEditingTenant(prev => ({ ...prev, logoUrl: '' }));
      } else {
        setNewCompanyForm(prev => ({ ...prev, logoUrl: '' }));
      }
      showToast('Logo removed successfully', 'success');
    } catch (err) {
      console.error('Error removing logo:', err);
      if (isEdit) {
        setEditingTenant(prev => ({ ...prev, logoUrl: '' }));
      } else {
        setNewCompanyForm(prev => ({ ...prev, logoUrl: '' }));
      }
      showToast('Logo removed', 'success');
    }
  };

  const getTenantPortalUrl = (tenantId) => {
    if (!tenantId || tenantId === 'prophecy') {
      return `${window.location.origin}/login`;
    }
    const host = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port ? `:${window.location.port}` : '';

    if (host.includes('devprophecyerp.com')) {
      return `${protocol}//${tenantId}.devprophecyerp.com${port}/login`;
    }
    if (host.includes('prophecytechs.com')) {
      return `${protocol}//${tenantId}.prophecytechs.com${port}/login`;
    }
    return `${window.location.origin}/login?company=${tenantId}`;
  };

  const handleCopyLink = (tenantId) => {
    const link = getTenantPortalUrl(tenantId);
    navigator.clipboard.writeText(link);
    showToast(`Copied portal link: ${link}`);
  };

  // Filtered tenants by search query
  const filteredTenants = tenants.filter(t => {
    const q = searchQuery.toLowerCase();
    return (
      (t.companyName && t.companyName.toLowerCase().includes(q)) ||
      (t.tenantId && t.tenantId.toLowerCase().includes(q))
    );
  });

  if (!isProphecy || !isAdmin) {
    return (
      <div className="tenant-mgmt-container" style={{ textAlign: 'center', paddingTop: '80px' }}>
        <LuShieldAlert size={48} color="#ef4444" style={{ marginBottom: '16px' }} />
        <h2>Access Restricted</h2>
        <p style={{ color: '#64748b' }}>Only Prophecy ERP administrators can access SaaS tenant configuration.</p>
      </div>
    );
  }

  return (
    <div className="tenant-mgmt-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`tenant-toast-notification ${toast.type}`}>
          {toast.type === 'success' ? <LuCheck size={18} /> : <LuX size={18} />}
          <span>{typeof toast.message === 'object' ? JSON.stringify(toast.message) : String(toast.message)}</span>
        </div>
      )}

      {/* Page Header */}
      <div className="tenant-mgmt-header">
        <div className="tenant-mgmt-title-group">
          <h1>
            <LuBuilding2 size={28} color="#2563eb" />
            Company & Module Access Management
          </h1>
          <p>Register new client tenants, configure brand identity, and control active ERP module subscriptions.</p>
        </div>

        <div className="tenant-mgmt-actions">
          <div className="tenant-search-box">
            <LuSearch className="tenant-search-icon" />
            <input
              type="text"
              className="tenant-search-input"
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button className="btn-primary-tenant" onClick={() => setIsAddModalOpen(true)}>
            <LuPlus size={18} />
            Add New Company
          </button>
        </div>
      </div>

      {/* Company Cards Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
          <p>Loading registered companies...</p>
        </div>
      ) : filteredTenants.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <LuBuilding2 size={40} color="#94a3b8" style={{ marginBottom: '12px' }} />
          <h3>No companies found</h3>
          <p style={{ color: '#64748b' }}>Try adjusting your search query or onboard a new company.</p>
        </div>
      ) : (
        <div className="tenant-cards-grid">
          {filteredTenants.map(tenant => (
            <div className="tenant-card" key={tenant.tenantId}>
              {/* Primary Color Accent Top Bar */}
              <div 
                className="tenant-card-top-bar" 
                style={{ backgroundColor: tenant.primaryColor || '#1976D2' }}
              />

              <div className="tenant-card-body">
                {/* Header & Logo */}
                <div className="tenant-card-header">
                  <div className="tenant-logo-group">
                    {tenant.logoUrl ? (
                      <img 
                        src={tenant.logoUrl} 
                        alt={tenant.companyName} 
                        className="tenant-logo-img"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="tenant-logo-placeholder">
                        {tenant.companyName ? tenant.companyName.charAt(0).toUpperCase() : 'C'}
                      </div>
                    )}

                    <div className="tenant-title-block">
                      <h3>{tenant.companyName}</h3>
                      <span className="tenant-slug-badge">{tenant.tenantId}</span>
                    </div>
                  </div>

                  <span className={`tenant-status-pill ${tenant.status === 'active' ? 'active' : 'inactive'}`}>
                    {tenant.status}
                  </span>
                </div>

                {/* Quick Info Row */}
                <div className="tenant-stats-row">
                  <div className="tenant-stats-item">
                    <LuUsers size={15} />
                    <span><strong>{tenant.userCount}</strong> Users</span>
                  </div>
                  {tenant.isHost && (
                    <div className="tenant-stats-item">
                      <span style={{ color: '#2563eb', fontWeight: 600 }}>Host ERP System</span>
                    </div>
                  )}
                </div>

                {/* Subscribed Modules Section */}
                <div className="tenant-modules-section">
                  <div className="tenant-modules-title">
                    <span>Active Modules ({tenant.allowedModules ? tenant.allowedModules.length : 0})</span>
                  </div>
                  <div className="tenant-modules-tags">
                    {tenant.isHost ? (
                      <span className="module-badge host">Full ERP Access (All Modules)</span>
                    ) : tenant.allowedModules && tenant.allowedModules.length > 0 ? (
                      tenant.allowedModules.map(mod => (
                        <span className="module-badge" key={mod}>
                          <LuCheck size={12} />
                          {mod}
                        </span>
                      ))
                    ) : (
                      <span className="module-badge-empty">No modules enabled</span>
                    )}
                  </div>
                </div>

                {/* Card Action Footer */}
                <div className="tenant-card-footer">
                  {tenant.isHost ? (
                    <div className="host-system-bar">
                      <span>Host ERP System</span>
                    </div>
                  ) : (
                    <button 
                      className="btn-card-action btn-manage-modules"
                      onClick={() => handleOpenModuleModal(tenant)}
                    >
                      <LuSlidersHorizontal size={15} />
                      Manage Modules
                    </button>
                  )}

                  <div className="tenant-card-actions-row">
                    <button 
                      className="btn-card-action" 
                      title="Edit company branding"
                      onClick={() => setEditingTenant({ ...tenant })}
                    >
                      <LuPencil size={14} />
                      Edit
                    </button>

                    <button 
                      className="btn-card-action btn-copy-link" 
                      title="Copy direct portal link"
                      onClick={() => handleCopyLink(tenant.tenantId)}
                    >
                      <LuCopy size={14} />
                      Link
                    </button>

                    <button 
                      className="btn-card-action btn-open-portal" 
                      title="Open portal"
                      onClick={() => window.open(getTenantPortalUrl(tenant.tenantId), '_blank')}
                    >
                      <LuExternalLink size={14} />
                    </button>

                    {!tenant.isHost && (
                      <button 
                        className="btn-card-action btn-delete-tenant" 
                        title="Delete company"
                        disabled={deletingTenantId === tenant.tenantId}
                        onClick={() => handleDeleteCompany(tenant)}
                      >
                        <LuTrash2 size={14} />
                        {deletingTenantId === tenant.tenantId ? 'Deleting...' : 'Delete'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL 1: Manage Modules */}
      {moduleModalTenant && (
        <div className="tenant-modal-overlay">
          <div className="tenant-modal-card">
            <div className="tenant-modal-header">
              <h2>Module Permissions for {moduleModalTenant.companyName}</h2>
              <button className="tenant-modal-close" onClick={() => setModuleModalTenant(null)}>
                <LuX />
              </button>
            </div>

            <div className="tenant-modal-body">
              <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                Select which modules this company has purchased or is authorized to access. Unchecked modules will be completely hidden from their sidebar.
              </p>

              <div className="modal-modules-grid">
                {DEFAULT_MODULE_OPTIONS.map(mod => {
                  const isChecked = selectedModules.some(m => m.toLowerCase() === mod.toLowerCase());
                  return (
                    <div 
                      className={`module-checkbox-card ${isChecked ? 'selected' : ''}`}
                      key={mod}
                      onClick={() => handleToggleModuleInModal(mod)}
                    >
                      <input 
                        type="checkbox" 
                        checked={isChecked}
                        onChange={() => {}} // handled by card onClick
                      />
                      <span className="module-checkbox-label">{mod}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="tenant-modal-footer">
              <button 
                className="btn-modal-cancel" 
                onClick={() => setModuleModalTenant(null)}
                disabled={savingModules}
              >
                Cancel
              </button>
              <button 
                className="btn-modal-submit" 
                onClick={handleSaveModules}
                disabled={savingModules}
              >
                {savingModules ? 'Saving Changes...' : 'Save Permissions'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: Add New Company */}
      {isAddModalOpen && (
        <div className="tenant-modal-overlay">
          <div className="tenant-modal-card">
            <div className="tenant-modal-header">
              <h2>Onboard New Tenant Company</h2>
              <button className="tenant-modal-close" onClick={() => setIsAddModalOpen(false)}>
                <LuX />
              </button>
            </div>

            <form onSubmit={handleAddCompany}>
              <div className="tenant-modal-body">
                <div className="form-group-tenant">
                  <label>Tenant ID (URL Slug) *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. acme, zenith, cognifyar"
                    value={newCompanyForm.tenantId}
                    onChange={(e) => setNewCompanyForm({ ...newCompanyForm, tenantId: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
                  />
                  <span className="form-help-text">Subdomain: <strong>{newCompanyForm.tenantId || 'slug'}.devprophecyerp.com</strong> &bull; URL: <strong>/login?company={newCompanyForm.tenantId || 'slug'}</strong></span>
                </div>

                <div className="form-group-tenant">
                  <label>Company Display Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Acme Corporation"
                    value={newCompanyForm.companyName}
                    onChange={(e) => setNewCompanyForm({ ...newCompanyForm, companyName: e.target.value })}
                  />
                </div>

                <div className="form-group-tenant">
                  <label>Company Logo</label>
                  <div className="tenant-logo-upload-wrapper">
                    <div className="tenant-logo-preview-box">
                      {newCompanyForm.logoUrl ? (
                        <img 
                          src={newCompanyForm.logoUrl} 
                          alt="Preview" 
                          className="tenant-logo-preview-img"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <LuImage size={24} color="#94a3b8" />
                      )}
                    </div>
                    <div className="tenant-logo-upload-actions">
                      <div className="tenant-logo-buttons-row">
                        <label className="btn-tenant-upload">
                          <LuUpload size={15} />
                          {uploadingLogo ? 'Uploading...' : 'Upload Logo Image'}
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            disabled={uploadingLogo}
                            onChange={(e) => {
                              if (e.target.files?.[0]) handleLogoUpload(e.target.files[0], false);
                            }}
                          />
                        </label>
                        {newCompanyForm.logoUrl && (
                          <button
                            type="button"
                            className="btn-tenant-delete-logo"
                            title="Remove uploaded logo"
                            onClick={() => handleDeleteLogo(false)}
                          >
                            <LuTrash2 size={14} />
                            Remove Logo
                          </button>
                        )}
                      </div>
                      <span className="tenant-upload-hint">PNG, JPG, SVG, WebP (max 5MB) - auto-populates URL below</span>
                    </div>
                  </div>
                </div>

                <div className="form-group-tenant">
                  <label>Company Logo URL / File Path</label>
                  <input
                    type="text"
                    placeholder="e.g. /logos/acme.png or https://example.com/logo.png"
                    value={newCompanyForm.logoUrl}
                    onChange={(e) => setNewCompanyForm({ ...newCompanyForm, logoUrl: e.target.value })}
                  />
                  <span className="form-help-text">Auto-populated by file upload above or manually editable</span>
                </div>

                <div className="form-group-tenant">
                  <label>Primary Brand Color</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="color"
                      style={{ width: '48px', height: '40px', padding: '2px', cursor: 'pointer' }}
                      value={newCompanyForm.primaryColor}
                      onChange={(e) => setNewCompanyForm({ ...newCompanyForm, primaryColor: e.target.value })}
                    />
                    <input
                      type="text"
                      style={{ flex: 1 }}
                      value={newCompanyForm.primaryColor}
                      onChange={(e) => setNewCompanyForm({ ...newCompanyForm, primaryColor: e.target.value })}
                    />
                  </div>
                </div>

                {/* Modules Selection */}
                <div className="form-group-tenant">
                  <label>Subscribed Modules</label>
                  <div className="modal-modules-grid">
                    {DEFAULT_MODULE_OPTIONS.map(mod => {
                      const isChecked = newCompanyForm.allowedModules.includes(mod);
                      return (
                        <div 
                          className={`module-checkbox-card ${isChecked ? 'selected' : ''}`}
                          key={mod}
                          onClick={() => {
                            const updated = isChecked
                              ? newCompanyForm.allowedModules.filter(m => m !== mod)
                              : [...newCompanyForm.allowedModules, mod];
                            setNewCompanyForm({ ...newCompanyForm, allowedModules: updated });
                          }}
                        >
                          <input 
                            type="checkbox" 
                            checked={isChecked}
                            onChange={() => {}}
                          />
                          <span className="module-checkbox-label">{mod}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Optional Initial Admin Account */}
                <div style={{ marginTop: '10px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#0f172a' }}>Initial Admin User (Optional)</h4>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '10px' }}>
                    <div className="form-group-tenant">
                      <label>First Name</label>
                      <input
                        type="text"
                        placeholder="John"
                        value={newCompanyForm.adminFirstName}
                        onChange={(e) => setNewCompanyForm({ ...newCompanyForm, adminFirstName: e.target.value })}
                      />
                    </div>
                    <div className="form-group-tenant">
                      <label>Last Name</label>
                      <input
                        type="text"
                        placeholder="Doe"
                        value={newCompanyForm.adminLastName}
                        onChange={(e) => setNewCompanyForm({ ...newCompanyForm, adminLastName: e.target.value })}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="form-group-tenant">
                      <label>Admin Email</label>
                      <input
                        type="email"
                        placeholder="admin@company.com"
                        value={newCompanyForm.adminEmail}
                        onChange={(e) => setNewCompanyForm({ ...newCompanyForm, adminEmail: e.target.value })}
                      />
                    </div>
                    <div className="form-group-tenant">
                      <label>Admin Password</label>
                      <input
                        type="password"
                        placeholder="Temporary Password"
                        value={newCompanyForm.adminPassword}
                        onChange={(e) => setNewCompanyForm({ ...newCompanyForm, adminPassword: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="tenant-modal-footer">
                <button 
                  type="button" 
                  className="btn-modal-cancel" 
                  onClick={() => setIsAddModalOpen(false)}
                  disabled={addingCompany}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-modal-submit" 
                  disabled={addingCompany}
                >
                  {addingCompany ? 'Creating Company...' : 'Create Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: Edit Company Details */}
      {editingTenant && (
        <div className="tenant-modal-overlay">
          <div className="tenant-modal-card">
            <div className="tenant-modal-header">
              <h2>Edit {editingTenant.companyName}</h2>
              <button className="tenant-modal-close" onClick={() => setEditingTenant(null)}>
                <LuX />
              </button>
            </div>

            <form onSubmit={handleUpdateCompany}>
              <div className="tenant-modal-body">
                <div className="form-group-tenant">
                  <label>Tenant ID (Permanent Slug)</label>
                  <input
                    type="text"
                    disabled
                    value={editingTenant.tenantId}
                    style={{ background: '#f1f5f9', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group-tenant">
                  <label>Company Display Name *</label>
                  <input
                    type="text"
                    required
                    value={editingTenant.companyName}
                    onChange={(e) => setEditingTenant({ ...editingTenant, companyName: e.target.value })}
                  />
                </div>

                <div className="form-group-tenant">
                  <label>Company Logo</label>
                  <div className="tenant-logo-upload-wrapper">
                    <div className="tenant-logo-preview-box">
                      {editingTenant.logoUrl ? (
                        <img 
                          src={editingTenant.logoUrl} 
                          alt="Preview" 
                          className="tenant-logo-preview-img"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <LuImage size={24} color="#94a3b8" />
                      )}
                    </div>
                    <div className="tenant-logo-upload-actions">
                      <div className="tenant-logo-buttons-row">
                        <label className="btn-tenant-upload">
                          <LuUpload size={15} />
                          {uploadingLogo ? 'Uploading...' : 'Upload Replacement Logo'}
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            disabled={uploadingLogo}
                            onChange={(e) => {
                              if (e.target.files?.[0]) handleLogoUpload(e.target.files[0], true);
                            }}
                          />
                        </label>
                        {editingTenant.logoUrl && (
                          <button
                            type="button"
                            className="btn-tenant-delete-logo"
                            title="Remove current logo"
                            onClick={() => handleDeleteLogo(true)}
                          >
                            <LuTrash2 size={14} />
                            Remove Logo
                          </button>
                        )}
                      </div>
                      <span className="tenant-upload-hint">Upload new image or remove current logo</span>
                    </div>
                  </div>
                </div>

                <div className="form-group-tenant">
                  <label>Company Logo URL / File Path</label>
                  <input
                    type="text"
                    placeholder="e.g. /logos/acme.png or https://example.com/logo.png"
                    value={editingTenant.logoUrl || ''}
                    onChange={(e) => setEditingTenant({ ...editingTenant, logoUrl: e.target.value })}
                  />
                  <span className="form-help-text">Auto-populated by file upload above or manually editable</span>
                </div>

                <div className="form-group-tenant">
                  <label>Primary Brand Color</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="color"
                      style={{ width: '48px', height: '40px', padding: '2px', cursor: 'pointer' }}
                      value={editingTenant.primaryColor || '#1976D2'}
                      onChange={(e) => setEditingTenant({ ...editingTenant, primaryColor: e.target.value })}
                    />
                    <input
                      type="text"
                      style={{ flex: 1 }}
                      value={editingTenant.primaryColor || '#1976D2'}
                      onChange={(e) => setEditingTenant({ ...editingTenant, primaryColor: e.target.value })}
                    />
                  </div>
                </div>

                {!editingTenant.isHost && (
                  <div className="form-group-tenant">
                    <label>Tenant Status</label>
                    <select
                      value={editingTenant.status}
                      onChange={(e) => setEditingTenant({ ...editingTenant, status: e.target.value })}
                    >
                      <option value="active">Active (Access Allowed)</option>
                      <option value="inactive">Inactive / Suspended (Access Blocked)</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="tenant-modal-footer">
                <button 
                  type="button" 
                  className="btn-modal-cancel" 
                  onClick={() => setEditingTenant(null)}
                  disabled={updatingCompany}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-modal-submit" 
                  disabled={updatingCompany}
                >
                  {updatingCompany ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;

